import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type {
  BoosterMarketingRow,
  BoosterType,
  CampagneBoostRow,
  CampagneBoostStatut,
  PageMarketingRow,
  PlateformeMarketing,
  RoiFormationB2cRow,
  RevenuStrategixRow,
} from '@/lib/database.types';

// Sprint 7.1 — Hooks B2C Marketing : pages, boosters, campagnes, ROI.
// Leads dédié au Sprint 7.2 (workflow plus complexe).

// ===== Pages marketing =====

export function usePagesMarketing(entrepriseId: string | null) {
  return useQuery({
    queryKey: ['pages_marketing', entrepriseId ?? '__all__'],
    enabled: !!entrepriseId,
    queryFn: async (): Promise<PageMarketingRow[]> => {
      if (!entrepriseId) return [];
      const { data, error } = await supabase
        .from('pages_marketing')
        .select('*')
        .eq('entreprise_id', entrepriseId)
        .order('plateforme', { ascending: true })
        .order('nom', { ascending: true });
      if (error) throw error;
      return data as unknown as PageMarketingRow[];
    },
  });
}

export interface UpsertPageMarketingInput {
  id?: string;
  entreprise_id: string;
  plateforme: PlateformeMarketing;
  nom: string;
  url?: string | null;
  owner_user_id?: string | null;
  is_active?: boolean;
}

export function useUpsertPageMarketing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertPageMarketingInput): Promise<PageMarketingRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('pages_marketing')
          .update(rest as never)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data as unknown as PageMarketingRow;
      }
      const { data, error } = await supabase
        .from('pages_marketing')
        .insert(rest as never)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as PageMarketingRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pages_marketing'] });
    },
  });
}

export function useDeletePageMarketing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('pages_marketing').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pages_marketing'] });
    },
  });
}

// ===== Boosters marketing =====

export function useBoostersMarketing(entrepriseId: string | null) {
  return useQuery({
    queryKey: ['boosters_marketing', entrepriseId ?? '__all__'],
    enabled: !!entrepriseId,
    queryFn: async (): Promise<BoosterMarketingRow[]> => {
      if (!entrepriseId) return [];
      const { data, error } = await supabase
        .from('boosters_marketing')
        .select('*')
        .eq('entreprise_id', entrepriseId)
        .order('is_active', { ascending: false })
        .order('nom', { ascending: true });
      if (error) throw error;
      return data as unknown as BoosterMarketingRow[];
    },
  });
}

export interface UpsertBoosterInput {
  id?: string;
  entreprise_id: string;
  nom: string;
  type: BoosterType;
  user_id?: string | null;
  ccp_compte?: string | null;
  telephone?: string | null;
  email?: string | null;
  notes?: string | null;
  is_active?: boolean;
}

export function useUpsertBooster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertBoosterInput): Promise<BoosterMarketingRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('boosters_marketing')
          .update(rest as never)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data as unknown as BoosterMarketingRow;
      }
      const { data, error } = await supabase
        .from('boosters_marketing')
        .insert(rest as never)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as BoosterMarketingRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['boosters_marketing'] });
    },
  });
}

export function useDeleteBooster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('boosters_marketing').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['boosters_marketing'] });
    },
  });
}

// ===== Campagnes boost =====

export function useCampagnesBoost(filter: {
  entrepriseId: string | null;
  formationId?: string | null;
  statut?: CampagneBoostStatut | null;
}) {
  return useQuery({
    queryKey: ['campagnes_boost', filter],
    enabled: !!filter.entrepriseId,
    queryFn: async (): Promise<CampagneBoostRow[]> => {
      if (!filter.entrepriseId) return [];
      let q = supabase
        .from('campagnes_boost')
        .select('*')
        .eq('entreprise_id', filter.entrepriseId)
        .order('date_debut', { ascending: false });
      if (filter.formationId) q = q.eq('formation_id', filter.formationId);
      if (filter.statut) q = q.eq('statut', filter.statut);
      const { data, error } = await q;
      if (error) throw error;
      return data as unknown as CampagneBoostRow[];
    },
  });
}

export interface UpsertCampagneBoostInput {
  id?: string;
  entreprise_id: string;
  formation_id: string;
  page_id?: string | null;
  booster_id?: string | null;
  montant: number;
  date_debut: string;
  date_fin?: string | null;
  statut?: CampagneBoostStatut;
  nb_messages_recus?: number;
  nb_inscrits?: number;
  notes?: string | null;
}

export function useUpsertCampagneBoost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertCampagneBoostInput): Promise<CampagneBoostRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('campagnes_boost')
          .update(rest as never)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data as unknown as CampagneBoostRow;
      }
      const { data, error } = await supabase
        .from('campagnes_boost')
        .insert(rest as never)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as CampagneBoostRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['campagnes_boost'] });
    },
  });
}

export function useDeleteCampagneBoost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('campagnes_boost').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['campagnes_boost'] });
    },
  });
}

// ===== Vues d'agrégation (lecture seule) =====

export function useRoiFormationB2c(entrepriseId: string | null) {
  return useQuery({
    queryKey: ['v_roi_formation_b2c', entrepriseId ?? '__all__'],
    enabled: !!entrepriseId,
    queryFn: async (): Promise<RoiFormationB2cRow[]> => {
      if (!entrepriseId) return [];
      const { data, error } = await supabase
        .from('v_roi_formation_b2c')
        .select('*')
        .eq('entreprise_id', entrepriseId)
        .order('marge_brute_kost_dzd', { ascending: false });
      if (error) throw error;
      return data as unknown as RoiFormationB2cRow[];
    },
  });
}

export function useRevenusStrategix() {
  return useQuery({
    queryKey: ['v_revenus_strategix'],
    queryFn: async (): Promise<RevenuStrategixRow[]> => {
      const { data, error } = await supabase
        .from('v_revenus_strategix')
        .select('*')
        .order('mois', { ascending: false })
        .limit(24);
      if (error) throw error;
      return data as unknown as RevenuStrategixRow[];
    },
  });
}
